import type {
	TConversation,
	TTimestampValue,
} from "../types/agent";

const DAY_IN_MILLIS = 24 * 60 * 60 * 1000;
const SIDEBAR_BUCKET_ORDER = [
	"Today",
	"Last 7 days",
	"Last 30 days",
	"Older chats",
] as const;

type TSidebarBucket = (typeof SIDEBAR_BUCKET_ORDER)[number];

type TGroupConversationsByDateOptions = {
	now?: Date;
};

const createEmptyGroups = (): Record<
	TSidebarBucket,
	Array<TConversation>
> => ({
	Today: [],
	"Last 7 days": [],
	"Last 30 days": [],
	"Older chats": [],
});

const toDate = (
	value: TTimestampValue | null | undefined
): Date | null => {
	if (value === null || value === undefined) {
		return null;
	}

	if (typeof value === "number") {
		const millis =
			value < 1_000_000_000_000 ? value * 1000 : value;
		const parsedDate = new Date(millis);
		return Number.isNaN(parsedDate.getTime())
			? null
			: parsedDate;
	}

	const trimmedValue = value.trim();
	if (trimmedValue.length === 0) {
		return null;
	}

	const numericValue = Number(trimmedValue);
	if (Number.isFinite(numericValue)) {
		const millis =
			numericValue < 1_000_000_000_000
				? numericValue * 1000
				: numericValue;
		const parsedDate = new Date(millis);
		return Number.isNaN(parsedDate.getTime())
			? null
			: parsedDate;
	}

	const parsedDate = new Date(trimmedValue);
	return Number.isNaN(parsedDate.getTime())
		? null
		: parsedDate;
};

const startOfLocalDay = (date: Date) =>
	new Date(
		date.getFullYear(),
		date.getMonth(),
		date.getDate()
	);

const getConversationActivityDate = (
	conversation: TConversation
) => {
	const lastMessageSentAt:
		| TTimestampValue
		| null
		| undefined = conversation.last_message_sent_at;

	return (
		toDate(lastMessageSentAt) ??
		toDate(conversation.updated_at) ??
		toDate(conversation.created_at)
	);
};

const getConversationBucket = (
	conversation: TConversation,
	todayStart: Date
): TSidebarBucket => {
	const bucketDate =
		getConversationActivityDate(conversation);
	if (bucketDate === null) {
		return "Older chats";
	}

	const conversationDayStart = startOfLocalDay(bucketDate);
	const diffInDays = Math.floor(
		(todayStart.getTime() -
			conversationDayStart.getTime()) /
			DAY_IN_MILLIS
	);

	if (diffInDays <= 0) {
		return "Today";
	}
	if (diffInDays <= 7) {
		return "Last 7 days";
	}
	if (diffInDays <= 30) {
		return "Last 30 days";
	}
	return "Older chats";
};

const getConversationSortTime = (
	conversation: TConversation
) =>
	getConversationActivityDate(conversation)?.getTime() ?? 0;

const getConversationCreatedTime = (
	conversation: TConversation
) => toDate(conversation.created_at)?.getTime() ?? 0;

const compareConversationsByActivityTime = (
	a: TConversation,
	b: TConversation
) => {
	const updatedTimeDifference =
		getConversationSortTime(b) - getConversationSortTime(a);
	if (updatedTimeDifference !== 0) {
		return updatedTimeDifference;
	}

	const createdTimeDifference =
		getConversationCreatedTime(b) -
		getConversationCreatedTime(a);
	if (createdTimeDifference !== 0) {
		return createdTimeDifference;
	}

	return a.conv_id.localeCompare(b.conv_id);
};

export const groupConversationsByDate = (
	conversations: Array<TConversation>,
	options: TGroupConversationsByDateOptions = {}
) => {
	const sortedConversations = [...conversations].sort(
		compareConversationsByActivityTime
	);
	const todayStart = startOfLocalDay(
		options.now ?? new Date()
	);
	const groups = createEmptyGroups();

	sortedConversations.forEach((conversation) => {
		const bucket = getConversationBucket(
			conversation,
			todayStart
		);
		groups[bucket].push(conversation);
	});

	return groups;
};
